import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
  JOINEE_FORM_STEPS,
} from "../../config/joineeFormConstants";
import iconArrowLeft from "../../Assests/Images/joinee/form/icon-arrow-left.svg";
import iconLockStep from "../../Assests/Images/joinee/form/icon-lock-step.svg";
import pageHeaderBg from "../../Assests/Images/joinee/form/page-header-bg.png";
import {
  useUnsavedGuard,
  useUnsavedSaver,
  useUnsavedTracking,
} from "../Joinee/UnsavedChanges";

var TAB_SWITCH_MESSAGE = "Switching tabs will discard them.";

var STEP_NAV_WIDTH = 220;
var STEP_NAV_GAP = 16;
var STEP_NAV_RADIUS = "12px";

/** Wide enough for the centred form plus the side menu on both sides, so the form keeps its normal width. */
function sideNavWideQuery(formMaxWidth) {
  return "@media (min-width: " + (formMaxWidth + 2 * (STEP_NAV_WIDTH + STEP_NAV_GAP) + 64) + "px)";
}

/** Figma side menu: "← New Joinee Form" card with the 5 sections; the selected one is #D3E6FF. */
function JoineeStepNav(props) {
  return (
    <Box
      component="nav"
      aria-label={props.title + " sections"}
      sx={[
        {
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "12px",
          pt: "12px",
          px: "14px",
          pb: "14px",
          width: { xs: "100%", md: STEP_NAV_WIDTH },
          flexShrink: 0,
          bgcolor: "#FFFFFF",
          border: "1px solid " + JOINEE_FORM_COLORS.border,
          borderRadius: STEP_NAV_RADIUS,
        },
      ].concat(props.sx || [])}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minHeight: 30 }}>
        {props.onBack ? (
          <IconButton aria-label="Back to home" onClick={props.onBack} size="small" sx={{ p: 0, width: 24, height: 24, flexShrink: 0 }}>
            <Box component="img" src={iconArrowLeft} alt="" sx={{ width: 24, height: 24 }} />
          </IconButton>
        ) : null}
        <Typography
          component="h2"
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 18,
            fontWeight: 800,
            lineHeight: "28px",
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            color: "#171A1C",
          }}
        >
          {props.title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(auto-fit, minmax(110px, 1fr))", md: "1fr" },
          gap: "6px",
          p: "6px",
          bgcolor: "#F1F2F4",
          borderRadius: "8px",
        }}
      >
        {JOINEE_FORM_STEPS.map(function (step, index) {
          var active = index === props.activeStep;
          var unlocked = props.isUnlocked(index);
          return (
            <ButtonBase
              key={step.id}
              disabled={!unlocked}
              aria-current={active ? "step" : undefined}
              onClick={function () {
                props.onSelect(index);
              }}
              sx={{
                minHeight: 36,
                px: "12px",
                py: "7px",
                borderRadius: "6px",
                bgcolor: active ? "#D3E6FF" : "#FFFFFF",
                color: active ? JOINEE_FORM_COLORS.navy : unlocked ? JOINEE_FORM_COLORS.label : JOINEE_FORM_COLORS.placeholder,
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 16,
                fontWeight: active ? 600 : 500,
                lineHeight: "22px",
                whiteSpace: "nowrap",
                transition: "background-color 120ms",
                "&:hover": { bgcolor: active ? "#D3E6FF" : "#F5F7FF" },
                "&.Mui-focusVisible": { outline: "2px solid " + JOINEE_FORM_COLORS.primary, outlineOffset: "1px" },
              }}
            >
              {step.label}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * One white card: bordered mesh header + form body (not a second card).
 * Steps span full header content width: first aligns with title row start,
 * last aligns with header/input right edge.
 */
function JoineeFormShell(props) {
  var activeStep = props.activeStep || 0;
  var onStepChange = props.onStepChange;
  var onBack = props.onBack;
  var title = props.title;
  var subtitle = props.subtitle;
  var footer = props.footer;
  var banner = props.banner;
  var maxWidth = props.maxWidth || 780;
  // Candidates may open any of the 5 steps freely (Figma / product).
  var freeStepNavigation = props.freeStepNavigation !== false;
  var guard = useUnsavedGuard();
  // Only candidates get a back arrow, so it doubles as the "track edits" switch.
  useUnsavedTracking(Boolean(onBack));

  function goToStep(index) {
    if (!onStepChange || index === activeStep) return;
    guard(function () {
      onStepChange(index);
    }, TAB_SWITCH_MESSAGE);
  }

  function isStepUnlocked(index) {
    var isDone = props.completedSteps ? props.completedSteps[index] : false;
    return freeStepNavigation || index === activeStep || isDone || index < activeStep;
  }

  var stepMeta = JOINEE_FORM_STEPS[activeStep] || JOINEE_FORM_STEPS[0];
  var headerTitle = title || stepMeta.title;
  var headerSubtitle = subtitle || stepMeta.subtitle;
  var sideNav = Boolean(props.sideNavTitle);
  var headerBack = sideNav ? null : onBack;
  var showTopSteps = !props.hideSteps && !sideNav;
  var wideQuery = sideNavWideQuery(maxWidth);

  var shell = (
    <Box
      className="joinee-form-shell"
      sx={{
        width: sideNav ? "100%" : { xs: "100%", md: "82%" },
        maxWidth: sideNav ? "none" : maxWidth,
        mx: sideNav ? 0 : "auto",
        flex: sideNav ? "1 1 auto" : undefined,
        minWidth: 0,
        [wideQuery]: sideNav ? { width: "82%", maxWidth: maxWidth, mx: "auto", flex: "none" } : {},
        height: { xs: "auto", md: "calc(100dvh - var(--joinee-shell-offset, 168px))" },
        maxHeight: { md: "calc(100dvh - var(--joinee-shell-offset, 168px))" },
        "@supports not (height: 100dvh)": {
          height: { md: "calc(100vh - var(--joinee-shell-offset, 168px))" },
          maxHeight: { md: "calc(100vh - var(--joinee-shell-offset, 168px))" },
        },
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        bgcolor: "#FFFFFF",
        border: "1px solid #E6E8EA",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header — bordered card inside the single shell card */}
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "12px",
          px: "12px",
          pt: "12px",
          pb: "16px",
          mx: "12px",
          mt: "12px",
          mb: 0,
          border: "1px solid #E6E8EA",
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={pageHeaderBg}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.78)",
            }}
          />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "12px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                flex: "1 1 auto",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  width: "100%",
                }}
              >
                {headerBack ? (
                  <IconButton
                    aria-label="Back to home"
                    onClick={function () {
                      guard(headerBack);
                    }}
                    size="small"
                    sx={{ p: 0, width: 24, height: 24 }}
                  >
                    <Box
                      component="img"
                      src={iconArrowLeft}
                      alt=""
                      sx={{ width: 24, height: 24 }}
                    />
                  </IconButton>
                ) : null}
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 24,
                    fontWeight: 600,
                    color: "#171A1C",
                    lineHeight: "36px",
                  }}
                >
                  {headerTitle}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: JOINEE_FORM_FONT,
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#848F9A",
                  lineHeight: 1.14,
                  pl: headerBack ? "28px" : 0,
                }}
              >
                {headerSubtitle}
              </Typography>
            </Box>
            {props.headerAction ? (
              <Box sx={{ flexShrink: 0, pt: "4px" }}>{props.headerAction}</Box>
            ) : null}
          </Box>

          {showTopSteps ? (
            <React.Fragment>
              <Box
                sx={{
                  borderTop: "1px solid #E6E8EA",
                  mt: "12px",
                  width: "100%",
                }}
              />

              {/* Steps: Candidate Info ←→ Insurance */}
              <Box
                sx={{
                  mt: "12px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: 0,
                  width: "100%",
                  minHeight: 28,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  rowGap: { xs: "8px", md: 0 },
                  boxSizing: "border-box",
                }}
              >
                {JOINEE_FORM_STEPS.map(function (step, index) {
                  var isActive = index === activeStep;
                  var isDone = props.completedSteps
                    ? props.completedSteps[index]
                    : false;
                  var isPast = index < activeStep;
                  var isUnlocked =
                    freeStepNavigation || isActive || isDone || isPast;
                  return (
                    <Box
                      key={step.id}
                      onClick={function () {
                        if (isUnlocked) {
                          goToStep(index);
                        }
                      }}
                      role="button"
                      tabIndex={isUnlocked ? 0 : -1}
                      aria-current={isActive ? "step" : undefined}
                      onKeyDown={function (e) {
                        if (!isUnlocked) {
                          return;
                        }
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToStep(index);
                        }
                      }}
                      sx={{
                        display: "inline-flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "8px",
                        columnGap: "8px",
                        cursor: isUnlocked ? "pointer" : "default",
                        height: 28,
                        flex: "0 0 auto",
                        minWidth: 0,
                        width: "auto",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: isUnlocked ? "#050A44" : "#FDFDFD",
                          border: isActive
                            ? "2px solid #000FB8"
                            : isUnlocked
                            ? "none"
                            : "1px solid #C7CCD1",
                          boxShadow: isActive
                            ? "0 0 0 3px rgba(5, 10, 68, 0.18)"
                            : "none",
                          color: "#F9FAFA",
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 15,
                          fontWeight: isActive ? 600 : 400,
                          lineHeight: "normal",
                        }}
                      >
                        {isUnlocked ? (
                          index + 1
                        ) : (
                          <Box
                            component="img"
                            src={iconLockStep}
                            alt=""
                            sx={{
                              width: 15,
                              height: 15,
                              display: "block",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 15,
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? "#050A44" : "#171A1C",
                          lineHeight: "28px",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          m: 0,
                          p: 0,
                          borderBottom: isActive
                            ? "2px solid #050A44"
                            : "2px solid transparent",
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </React.Fragment>
          ) : null}
        </Box>
      </Box>

      {/* Form body — same card, not a second card; same content padding as header */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {banner ? (
          <Box
            sx={{
              flexShrink: 0,
              px: "24px",
              pt: "12px",
              pb: 0,
              boxSizing: "border-box",
            }}
          >
            {banner}
          </Box>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: "24px",
            pt: "16px",
            pb: "16px",
            boxSizing: "border-box",
          }}
        >
          {props.children}
        </Box>

        {footer ? (
          <Box
            sx={{
              flexShrink: 0,
              position: "relative",
              zIndex: 2,
              borderTop: "1px solid #E6E8EA",
              px: "24px",
              py: 1.5,
              minHeight: 56,
              bgcolor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
              boxSizing: "border-box",
              boxShadow: "0 -4px 12px rgba(23, 26, 28, 0.04)",
            }}
          >
            {footer}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  if (!sideNav) {
    return shell;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: STEP_NAV_GAP + "px",
        width: { xs: "100%", md: "94%" },
        maxWidth: maxWidth + STEP_NAV_WIDTH + STEP_NAV_GAP,
        mx: "auto",
        position: "relative",
        [wideQuery]: { display: "block", width: "100%", maxWidth: "none" },
      }}
    >
      <JoineeStepNav
        sx={{
          [wideQuery]: {
            position: "absolute",
            top: 0,
            right: "calc(50% + min(41%, " + maxWidth / 2 + "px) + " + STEP_NAV_GAP + "px)",
          },
        }}
        title={props.sideNavTitle}
        activeStep={activeStep}
        isUnlocked={isStepUnlocked}
        onSelect={goToStep}
        onBack={
          onBack
            ? function () {
                guard(onBack);
              }
            : null
        }
      />
      {shell}
    </Box>
  );
}

export function JoineeFormFooterActions(props) {
  var guard = useUnsavedGuard();
  useUnsavedSaver(!props.hideDraft && props.onDraft ? props.onDraft : null);

  return (
    <React.Fragment>
      <Box>
        {props.showPrevious ? (
          <Button
            type="button"
            variant="outlined"
            onClick={function () {
              if (props.onPrevious) guard(props.onPrevious, props.previousMessage);
            }}
            disabled={props.disabled}
            sx={{
              fontFamily: JOINEE_FORM_FONT,
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              color: JOINEE_FORM_COLORS.label,
              borderColor: JOINEE_FORM_COLORS.border,
              borderRadius: "6px",
              minHeight: 36,
              px: 2,
            }}
          >
            Previous Page
          </Button>
        ) : (
          <span />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
        {!props.hideDraft ? (
          <Button
            type="button"
            variant="outlined"
            onClick={props.onDraft}
            disabled={props.disabled || props.drafting}
            sx={{
              fontFamily: JOINEE_FORM_FONT,
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              color: JOINEE_FORM_COLORS.label,
              borderColor: JOINEE_FORM_COLORS.border,
              borderRadius: "6px",
              minHeight: 36,
              px: 2,
            }}
          >
            {props.drafting ? "Saving..." : "Save as Draft"}
          </Button>
        ) : null}
        {!props.hideNext ? (
          <Button
            type="button"
            variant="contained"
            onClick={props.onNext}
            disabled={props.disabled || props.saving}
            sx={{
              fontFamily: JOINEE_FORM_FONT,
              fontSize: 14,
              fontWeight: 400,
              textTransform: "none",
              bgcolor: JOINEE_FORM_COLORS.primary,
              color: "#fff",
              borderRadius: "6px",
              minHeight: 36,
              px: 2.5,
              boxShadow: "none",
              "&:hover": { bgcolor: "#000FB8", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "#C7CCD1",
                color: "#F9FAFA",
                opacity: 1,
              },
            }}
          >
            {props.saving
              ? "Saving..."
              : props.nextLabel || "Save and next"}
          </Button>
        ) : null}
      </Box>
    </React.Fragment>
  );
}

export default JoineeFormShell;
